import { isHttpClientError } from "../../../lib/http/errors";
import type { ItineraryEditor } from "../types";
import { validateEditorDates } from "./itineraryEditorService";
import { itineraryPersistedGraphFailureMessage, itineraryRailwayOvernightFailureMessage } from "./itineraryErrorService";

const fieldNames: Record<string, string> = {
  id: "标识", title: "标题", dayDate: "日期", day_date: "日期", nodeType: "点位类型", node_type: "点位类型",
  pointSpuId: "所选地点", point_spu_id: "所选地点", stayAreaId: "所选住宿", stay_area_id: "所选住宿",
  fromNodeId: "出发地", from_node_id: "出发地", toNodeId: "目的地", to_node_id: "目的地",
  departureAt: "出发时间", departure_at: "出发时间", arrivalAt: "到达时间", arrival_at: "到达时间",
  durationMinutes: "交通时长", duration_minutes: "交通时长", sortOrder: "排序", sort_order: "排序",
  customerNotices: "注意事项", customer_notices: "注意事项", content: "内容", description: "介绍",
};

/** 将字段校验位置转为天次、点位或交通位置，不展示响应中的输入值。 */
function validationLocation(location: unknown[], editor?: ItineraryEditor): string {
  const parts: string[] = [];
  for (let i = 0; i < location.length; i += 1) {
    const key = location[i];
    const index = location[i + 1];
    if (typeof index === "number") {
      if (key === "days") {
        const day = editor?.days[index];
        parts.push(`第${index + 1}天${day?.dayTitle ? `（${day.dayTitle}）` : ""}`);
      } else if (key === "nodes") parts.push(`第${index + 1}个点位`);
      else if (key === "segments") parts.push(`第${index + 1}条交通`);
      else parts.push(`第${index + 1}项`);
      i += 1;
    } else if (typeof key === "string" && fieldNames[key]) parts.push(fieldNames[key]);
  }
  return parts.join(" · ") || "行程字段";
}

/** 复用草稿日期校验解释具体原因；旧响应与当前草稿不一致时只解释服务端原因。 */
export function itineraryDateSaveErrorMessage(reason: string, editor?: ItineraryEditor): string | undefined {
  const reasons: Record<string, string> = {
    "itinerary-date-header-incomplete": "行程开始日期和结束日期少填了一项。",
    "day-date-without-itinerary-date": "每天已经填了日期，但整段行程还没有填写开始日期和结束日期。",
    "itinerary-date-range-invalid": "行程结束日期早于开始日期。",
    "itinerary-date-count-invalid": "从开始日期到结束日期算出的天数，与行程天数不一致。",
    "itinerary-day-date-invalid": "每日日期没有按第1天、第2天的顺序接着往下排。",
    "itinerary-date-format-invalid": "行程开始日期或结束日期不是有效日期。",
  };
  if (!Object.hasOwn(reasons, reason)) return undefined;
  const detail = editor && validateEditorDates(editor).find((issue) => issue.code === reason)?.detail;
  return `${detail || reasons[reason]}请核对行程天数和日期后重新保存。本次尚未保存，当前编辑内容已保留。如果还是不能保存，请把这条提示和下方的错误详情发给管理员。`;
}

/** 将保存错误转换为具体中文原因与恢复动作。 */
export function itinerarySaveErrorMessage(error: unknown, editor?: ItineraryEditor): string | undefined {
  if (error === null || error === undefined) return undefined;
  const message = error instanceof Error ? error.message.trim() : "";
  if (isHttpClientError(error)) {
    const railwayMessage = itineraryRailwayOvernightFailureMessage(message);
    if (railwayMessage) return `${railwayMessage}本次没有保存成功，修改还在当前页面。请先不要刷新或关闭页面；如果仍然无法保存，请把下方的错误详情发给管理员。`;
    const dateMessage = itineraryDateSaveErrorMessage(message, editor);
    if (dateMessage) return dateMessage;
    const persistedMessage = itineraryPersistedGraphFailureMessage(error, "save");
    if (persistedMessage) return persistedMessage;
    const body = error.responseBody;
    const detail: unknown = body && typeof body === "object" ? Reflect.get(body, "detail") : undefined;
    if (error.statusCode === 422 && Array.isArray(detail) && detail.length) {
      const reasons = detail.flatMap((item: unknown) => {
        if (!item || typeof item !== "object") return [];
        const loc: unknown = Reflect.get(item, "loc");
        const type: unknown = Reflect.get(item, "type");
        const raw: unknown = Reflect.get(item, "msg");
        if (!Array.isArray(loc)) return [];
        const reason = typeof type === "string" && type.startsWith("uuid")
          ? loc.at(-1) === "id" ? "系统无法识别这条记录，请把错误详情发给管理员处理" : "系统找不到所选的地点或交通，请重新选择"
          : type === "missing" ? "这一项还没填，请补充"
            : typeof type === "string" && /date|time/.test(type) ? "日期或时间没有填对，请重新填写"
              : typeof raw === "string" && /[\u4e00-\u9fff]/.test(raw) && !/[{}<>]/.test(raw)
                ? raw.replace(/^Value error,\s*/i, "") : "这一项没有填对，请检查后重新填写";
        return [`${validationLocation(loc, editor)}：${reason}。`];
      });
      if (reasons.length) return `这次没有保存成功，下面几项需要检查。你的修改还在当前页面。\n${[...new Set(reasons)].join("\n")}\n如果页面上无法修改，请把下方的错误详情发给管理员。`;
    }
    if (/[\u4e00-\u9fff]/.test(message) && !message.startsWith("{") && !message.startsWith("[")) return message;
    const graphMessages: Record<string, string> = {
      "segment-departure-date-invalid": "这次没有保存成功：系统还在使用旧的日期检查规则，请联系管理员更新。你不需要修改火车或航班的参考日期。",
      "segment-time-range-invalid": "这次没有保存成功：到达时间不能早于或等于出发时间。请检查日期和时间，跨夜到达要选第二天或更晚的日期。",
      "segment-duration-inconsistent": "这次没有保存成功：填写的车程时长，与出发到到达之间的时间对不上。请检查这段交通的时间。",
      "segment-day-reference-invalid": "这次没有保存成功：这段交通安排的天次，与出发地、目的地所在的天次对不上。请到“骨架编辑”检查这段交通。",
    };
    if (graphMessages[message]) return graphMessages[message];
    const codeMessages: Record<string, string> = {
      ITINERARY_INVALID_GRAPH: "这次没有保存成功：有些地点、先后顺序或交通安排对不上。修改还在当前页面，请查看上方的具体提示；如果看不出哪里有问题，请把下方的错误详情发给管理员，先不要刷新或关闭页面。",
      ITINERARY_REFERENCE_INVALID: "这次没有保存成功：有些地点或交通找不到对应记录。请检查最近修改的地点、出发地和目的地；如果无法处理，请把下方的错误详情发给管理员。",
      ITINERARY_READONLY: "行程已定稿或归档，当前修改尚未保存，请先核对行程状态。",
      ITINERARY_REVISION_CONFLICT: "行程版本已更新，当前编辑内容已保留，请核对后重试保存。",
      ITINERARY_SQLSERVER_SYNC_FAILED: "行程已保存在新系统，但旧系统还没有更新成功。请稍后重试保存；如果还是没有更新，请联系管理员。",
      ITINERARY_AI_TIMEOUT: "补写行程介绍花的时间太长，本次尚未保存，草稿已保留。请稍后重试保存，也可以到“内容编辑”自己补写。",
      ITINERARY_AI_INVALID_OUTPUT: "自动补写没有生成可用的行程介绍，本次尚未保存，草稿已保留。请重试保存，也可以到“内容编辑”自己补写。",
      ITINERARY_AI_CONFIG_MISSING: "自动补写功能还没有设置好，本次尚未保存，草稿已保留。请联系管理员，也可以到“内容编辑”自己补写。",
      ITINERARY_AI_AUTHENTICATION_FAILED: "系统暂时无法使用自动补写功能，本次尚未保存，草稿已保留。请联系管理员检查，也可以到“内容编辑”自己补写。",
      ITINERARY_AI_RATE_LIMITED: "自动补写服务正忙，或可用次数不足，本次尚未保存，草稿已保留。请稍后再试；一直失败时请联系管理员。",
      ITINERARY_AI_UPSTREAM: "自动补写暂时用不了，本次尚未保存，草稿已保留。请稍后重试保存，也可以到“内容编辑”自己补写。",
    };
    if (error.code && codeMessages[error.code]) return codeMessages[error.code];
    if (error.statusCode === 401) return "登录已失效，当前编辑内容已保留，请重新登录后再保存。";
    if (error.statusCode === 403) return "当前账号没有保存行程的权限，请联系管理员开通。";
    if (error.statusCode === 404) return "未找到该行程，可能已删除，请核对后再保存。";
    if (error.statusCode === 422) return "这次没有保存成功：有些内容没有填全或没有填对。修改还在当前页面，请检查地点、日期和交通时间；如果看不出问题，请把下方的错误详情发给管理员。";
    if (error.statusCode !== null && error.statusCode >= 500) return "系统处理保存时出了问题，现在还不能确定是否保存成功。修改还在当前页面，请点“重试保存”；如果仍然失败，请把下方的错误详情发给管理员。";
    if (error.statusCode === null) return "保存时连接断了，现在还不能确定是否保存成功。修改还在当前页面，请检查网络后点“重试保存”，先不要刷新或关闭页面。";
  }
  if (/[\u4e00-\u9fff]/.test(message) && !message.startsWith("{") && !message.startsWith("[")) return message;
  return "保存未完成，当前编辑内容已保留，请稍后重试；如仍失败，请联系管理员。";
}

/** 只公开可用于定位请求的元数据，禁止包含会话、原始响应或用户输入。 */
export function itinerarySaveErrorDiagnostic(error: unknown): string | undefined {
  const cause = error instanceof Error && !isHttpClientError(error) ? error.cause : error;
  if (!isHttpClientError(cause)) return undefined;
  const safeIdentifier = (value: string | null) => value && /^[a-zA-Z0-9_.:-]{1,128}$/.test(value) ? value : null;
  return [
    `HTTP 状态：${cause.statusCode ?? "未收到响应"}`,
    safeIdentifier(cause.code) ? `错误码：${safeIdentifier(cause.code)}` : null,
    safeIdentifier(cause.request.traceId) ? `追踪编号：${safeIdentifier(cause.request.traceId)}` : "追踪编号：未提供",
    /^[a-z][a-z0-9-]{2,100}$/.test(cause.message) ? `校验原因：${cause.message}` : null,
  ].filter(Boolean).join("\n");
}
